import { Box, Divider, Group, Stack } from '@mantine/core';
import Text from '@ui/components/Text/Text';
import WhiteBox from 'src/components/WhiteBox/WhiteBox';
import useSnackbarSystem from '@ui/systems/useSnackbarSystem';
import useNonMembersQuery from '../../queries/useNonMembersQuery';
import useRequestSeatForNonMemberMutation from '../../queries/useRequestSeatForNonMemberMutation';
import NonMemberRow from './NonMemberRow';

type Props = {
	workspaceID: number;
	isOwner: boolean;
	onMemberAdded: () => void;
};

const NonMembersSection = ( { workspaceID, isOwner, onMemberAdded }: Props ) => {
	const { data: nonMembers } = useNonMembersQuery( workspaceID );
	const snackbar = useSnackbarSystem();
	const requestSeatMutation = useRequestSeatForNonMemberMutation( {
		workspaceID,
		onSuccess: () => snackbar.showOutlinedSuccessMessage( {
			title: 'Request sent',
			information: 'Your account owner will be notified.'
		} ),
		onError: () => snackbar.showErrorMessage( {
			title: 'Something went wrong. Please try again.'
		} )
	} );

	return (
		<WhiteBox className="NonMembersSection">
			<Stack spacing={4}>
				<Text variant="h4">Project Collaborators</Text>
				<Text
					variant="body"
					style={{ color: 'var(--color-text-secondary)', lineHeight: '22px', letterSpacing: 0 }}
				>
					These collaborators do not have paid workspace seats. Editors new to VeryBusy get 30 days
					of temporary Editor access when they are added to a project. After that, they can still
					review, comment, and markup, but they can no longer upload rounds or create labels unless
					they are assigned a workspace seat.
				</Text>
			</Stack>
			<Group spacing={2} w="100%" position="apart" noWrap className="column-names">
				<Box w="216px">Name</Box>
				<Box w="168px">Status</Box>
				<Box w="114px">Last active</Box>
				<Box w="109px">Collaborations</Box>
				<Box w="111px">Actions</Box>
			</Group>
			<Divider color="gray.2" w="100%" h="1px" mt={10} mb={12} />
			<Stack spacing={16} w="100%">
				{( nonMembers ?? [] ).map( nonMember => (
					<NonMemberRow
						key={nonMember.id}
						workspaceID={workspaceID}
						nonMember={nonMember}
						isOwner={isOwner}
						onMemberAdded={onMemberAdded}
						onRequestSeat={requestSeatMutation.mutate}
						isRequestingSeat={requestSeatMutation.isLoading}
					/>
				) )}
			</Stack>
		</WhiteBox>
	);
};

export default NonMembersSection;
